import { useMemo } from 'react'
import { useChainId, useReadContracts } from 'wagmi'
import { erc20Abi, zeroAddress } from 'viem'

import { useMarketInfo } from '@/domain/market-info/useMarketInfo'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { QUERY_REFETCH_INTERVAL } from '@/config/consts'
import { APIActivity } from './useActivity'
import getTokenByAddress from '@/utils/tokens'
import { FRAX_ORACLE_ABI } from '@/config/abis/fraxOracleAbi.abi'
import { FRAXLEND_PAIR_ABI } from '@/config/abis/FraxlendPair.abi'

export interface UseTokenInfoParams {
  activitiesData?: APIActivity[]
}

export interface UseTokenInfoResult {
  tokens: Token[]
  isLoading: boolean
}

export function useTokenInfo({ activitiesData = [] }: UseTokenInfoParams): UseTokenInfoResult {
  const chainId = useChainId()
  const { marketInfo } = useMarketInfo({ chainId, refetchInterval: QUERY_REFETCH_INTERVAL })

  // Extract unique addresses from activities data
  const uniqueAddresses = useMemo(() => {
    const addressSet = new Set<string>()

    for (const activity of activitiesData) {
      // Add asset and collateral from assets object
      if (activity.assets?.asset) {
        addressSet.add(activity.assets.asset.toLowerCase())
      }
      if (activity.assets?.collateral) {
        addressSet.add(activity.assets.collateral.toLowerCase())
      }
    }

    return Array.from(addressSet)
  }, [activitiesData])

  // Extract unique pair addresses from isolated activities
  const uniquePairAddresses = useMemo(() => {
    const pairAddressSet = new Set<string>()

    for (const activity of activitiesData) {
      // Check if activity is isolated and has pairAddress
      if (activity.type === 'isolated' && activity.assets?.pairAddress) {
        pairAddressSet.add(activity.assets.pairAddress.toLowerCase())
      }
    }

    return Array.from(pairAddressSet)
  }, [activitiesData])

  // Find tokens from marketInfo first, then from tokens file, then fetch from contract
  const tokensFromMarketInfo = useMemo(() => {
    const tokens: Token[] = []
    const remainingAddresses: string[] = []

    for (const address of uniqueAddresses) {
      const reserve = marketInfo.reserves.find((r) => r.token.address.toLowerCase() === address.toLowerCase())

      if (reserve) {
        tokens.push(reserve.token)
      } else {
        remainingAddresses.push(address)
      }
    }

    return { tokens, remainingAddresses }
  }, [uniqueAddresses, marketInfo])

  // Find tokens from tokens file for remaining addresses
  const tokensFromFile = useMemo(() => {
    const tokens: Token[] = []
    const remainingAddresses: string[] = []

    for (const address of tokensFromMarketInfo.remainingAddresses) {
      const tokenInfo = getTokenByAddress(chainId, address)

      if (tokenInfo) {
        tokens.push(
          new Token({
            name: tokenInfo.name || tokenInfo.symbol,
            address: CheckedAddress(tokenInfo.address),
            decimals: tokenInfo.decimals,
            symbol: TokenSymbol(tokenInfo.symbol as string),
            unitPriceUsd: '0',
          }),
        )
      } else {
        remainingAddresses.push(address)
      }
    }

    return { tokens, remainingAddresses }
  }, [tokensFromMarketInfo.remainingAddresses, chainId])

  // Fetch remaining tokens from contract
  const { data: contractTokenData, isLoading: isLoadingContractData } = useReadContracts({
    contracts: tokensFromFile.remainingAddresses.flatMap((address) => [
      {
        address: address as `0x${string}`,
        abi: erc20Abi,
        functionName: 'symbol',
      },
      {
        address: address as `0x${string}`,
        abi: erc20Abi,
        functionName: 'decimals',
      },
    ]),
    query: {
      enabled: tokensFromFile.remainingAddresses.length > 0,
    },
  })

  // Create tokens from contract data
  const tokensFromContract = useMemo(() => {
    const tokens: Token[] = []

    if (contractTokenData) {
      for (let i = 0; i < tokensFromFile.remainingAddresses.length; i++) {
        const address = tokensFromFile.remainingAddresses[i]
        if (!address) continue

        const symbolResult = contractTokenData[i * 2]
        const decimalsResult = contractTokenData[i * 2 + 1]

        if (symbolResult?.result && decimalsResult?.result) {
          tokens.push(
            new Token({
              name: String(symbolResult.result),
              address: CheckedAddress(address),
              decimals: Number(decimalsResult.result),
              symbol: TokenSymbol(String(symbolResult.result)),
              unitPriceUsd: '0',
            }),
          )
        }
      }
    }

    return tokens
  }, [contractTokenData, tokensFromFile.remainingAddresses])

  // Fetch contract data for unique pair addresses
  const { data: pairContractData, isLoading: isPairDataLoading } = useReadContracts({
    contracts: uniquePairAddresses.flatMap((pairAddress) => [
      {
        address: pairAddress as `0x${string}`,
        abi: FRAXLEND_PAIR_ABI,
        functionName: 'exchangeRateInfo',
      },
      {
        address: pairAddress as `0x${string}`,
        abi: FRAXLEND_PAIR_ABI,
        functionName: 'getConstants',
      },
      {
        address: pairAddress as `0x${string}`,
        abi: FRAXLEND_PAIR_ABI,
        functionName: 'asset',
      },
      {
        address: pairAddress as `0x${string}`,
        abi: FRAXLEND_PAIR_ABI,
        functionName: 'collateralContract',
      },
    ]),
    query: {
      enabled: uniquePairAddresses.length > 0,
    },
  }) as { data: any[]; isLoading: boolean }

  const oracleContracts = useMemo(() => {
    if (!pairContractData) return []

    const contracts: Array<{
      address: `0x${string}`
      abi: typeof FRAX_ORACLE_ABI
      functionName: 'getPrices'
    } | null> = []

    for (let i = 0; i < uniquePairAddresses.length; i++) {
      const exchangeRateInfo = pairContractData[i * 4]?.result
      if (exchangeRateInfo && Array.isArray(exchangeRateInfo)) {
        const [oracleAddress] = exchangeRateInfo
        if (oracleAddress && oracleAddress !== zeroAddress) {
          contracts.push({
            address: oracleAddress as `0x${string}`,
            abi: FRAX_ORACLE_ABI,
            functionName: 'getPrices',
          })
        } else {
          contracts.push(null) // Placeholder for pairs without oracle
        }
      } else {
        contracts.push(null)
      }
    }

    return contracts.filter((contract): contract is NonNullable<typeof contract> => contract !== null)
  }, [pairContractData, uniquePairAddresses])

  const { data: oraclePricesData } = useReadContracts({
    contracts: oracleContracts,
    query: {
      enabled: oracleContracts.length > 0,
    },
  })

  // Combine all tokens
  const allTokens = useMemo(() => {
    return [...tokensFromMarketInfo.tokens, ...tokensFromFile.tokens, ...tokensFromContract]
  }, [tokensFromMarketInfo.tokens, tokensFromFile.tokens, tokensFromContract])

  // Calculate and update token prices after all tokens are loaded
  const tokensWithUpdatedPrices = useMemo(() => {
    if (!pairContractData || isPairDataLoading || isLoadingContractData) {
      return allTokens
    }

    const updatedTokens = [...allTokens]

    // Process each pair
    uniquePairAddresses.forEach((_, i) => {
      const exchangeRateInfo = pairContractData[i * 4]?.result as any[]
      const constants = pairContractData[i * 4 + 1]?.result as any[]
      const assetContract = pairContractData[i * 4 + 2]?.result as string
      const collateralContract = pairContractData[i * 4 + 3]?.result as string

      if (!exchangeRateInfo || !constants || !assetContract || !collateralContract) return

      const [oracleAddress, _maxOracleDeviation, _oracleLastTimestamp, lowExchangeRate] = exchangeRateInfo
      const [
        _LTV_PRECISION,
        _LIQ_PRECISION,
        _UTIL_PREC,
        _FEE_PRECISION,
        _EXCHANGE_PRECISION,
        _DEVIATION_PRECISION,
        _RATE_PRECISION,
        _MAX_PROTOCOL_FEE,
      ] = constants

      // Find tokens for this pair
      const assetToken = updatedTokens.find((token) => token.address.toLowerCase() === assetContract.toLowerCase())
      const collateralToken = updatedTokens.find(
        (token) => token.address.toLowerCase() === collateralContract.toLowerCase(),
      )

      if (!assetToken || !collateralToken) return

      // Get oracle prices if available
      const oracleIndex = oracleContracts.findIndex((contract) => contract?.address === oracleAddress)
      const directOraclePrices = oracleIndex >= 0 ? (oraclePricesData?.[oracleIndex]?.result as unknown as any[]) : null

      // Calculate current exchange rate
      const currentExchangeRate =
        (Number(directOraclePrices ? Number(directOraclePrices[1]) : Number(lowExchangeRate)) /
          Number(_EXCHANGE_PRECISION)) *
        10 ** (assetToken.decimals - collateralToken.decimals)

      // Get existing prices from market info
      const collateralReserveToken = marketInfo.reserves.find(
        (r) => r.token.address.toLowerCase() === collateralContract.toLowerCase(),
      )
      const assetReserveToken = marketInfo.reserves.find(
        (r) => r.token.address.toLowerCase() === assetContract.toLowerCase(),
      )

      let collateralPrice = collateralReserveToken?.priceInUSD.toString() || '0'
      let assetPrice = assetReserveToken?.priceInUSD.toString() || '0'

      // Calculate prices if one or both are missing
      if (collateralPrice === '0' && assetPrice !== '0') {
        collateralPrice = (Number(assetPrice) * Number(1 / currentExchangeRate)).toString()
      } else if (assetPrice === '0' && collateralPrice !== '0') {
        assetPrice = (Number(collateralPrice) / currentExchangeRate).toString()
      } else if (collateralPrice === '0' && assetPrice === '0' && directOraclePrices) {
        // Both prices are 0, use oracle if available
        collateralPrice = '0'
        assetPrice = '0'
      }

      // Update tokens with calculated prices if they currently have unitPriceUsd of '0'
      const assetTokenIndex = updatedTokens.findIndex(
        (token) => token.address.toLowerCase() === assetContract.toLowerCase(),
      )
      const collateralTokenIndex = updatedTokens.findIndex(
        (token) => token.address.toLowerCase() === collateralContract.toLowerCase(),
      )

      if (
        assetTokenIndex >= 0 &&
        updatedTokens[assetTokenIndex] &&
        updatedTokens[assetTokenIndex].unitPriceUsd.isZero() &&
        assetPrice !== '0'
      ) {
        const assetTokenToUpdate = updatedTokens[assetTokenIndex]
        updatedTokens[assetTokenIndex] = new Token({
          name: assetTokenToUpdate.name,
          address: assetTokenToUpdate.address,
          decimals: assetTokenToUpdate.decimals,
          symbol: assetTokenToUpdate.symbol,
          unitPriceUsd: assetPrice,
        })
      }

      if (
        collateralTokenIndex >= 0 &&
        updatedTokens[collateralTokenIndex] &&
        updatedTokens[collateralTokenIndex].unitPriceUsd.isZero() &&
        collateralPrice !== '0'
      ) {
        const collateralTokenToUpdate = updatedTokens[collateralTokenIndex]
        updatedTokens[collateralTokenIndex] = new Token({
          name: collateralTokenToUpdate.name,
          address: collateralTokenToUpdate.address,
          decimals: collateralTokenToUpdate.decimals,
          symbol: collateralTokenToUpdate.symbol,
          unitPriceUsd: collateralPrice,
        })
      }
    })

    return updatedTokens
  }, [
    allTokens,
    pairContractData,
    isPairDataLoading,
    isLoadingContractData,
    uniquePairAddresses,
    oraclePricesData,
    oracleContracts,
    marketInfo,
  ])

  return {
    tokens: tokensWithUpdatedPrices,
    isLoading: isLoadingContractData || isPairDataLoading,
  }
}
